import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Button,
  Loading,
  InlineNotification,
  Dropdown,
  SelectSkeleton
} from 'carbon-components-react';
import { Pdf24, Csv24, Xls24 } from '@carbon/icons-react';
import {
  useReports,
  useReportConfig,
  useFilterState,
  useFilterOptions,
  useGenerateReport,
  createTokenAuthProvider
} from 'reporting-core-sdk/react';
import { FilterForm, ReportTable, ReportActions } from './components';
import {
  convertFiltersToApiFormat,
  areRequiredFiltersFilled,
  validateAllFilters
} from './utils';
import type { Filter } from './reportingTypes';

interface ReportsCrsViewerProps {
  getToken: () => Promise<string>;
  onTokenInvalid: () => void;
  baseUrl: string;
}

/**
 * ReportsCrsViewer component.
 *
 * WHAT: The main CRS (Cartinuum Reporting Tool) viewer that uses SDK hooks for data fetching.
 *
 * WHY: Separated from the auth wrapper so SDK hooks have stable config.
 * Uses a dynamic token getter to support automatic token refresh on 401.
 *
 * HOW: Uses SDK React hooks for reports list, config, filter state,
 * dynamic options, and report generation. Renders HTML in iframe.
 * The token getter is called on each API request, allowing fresh tokens
 * to be retrieved automatically when the old token expires.
 */
export const ReportsCrsViewer: React.FC<ReportsCrsViewerProps> = ({
  getToken,
  onTokenInvalid,
  baseUrl
}) => {
  // Wrap getToken to handle 401 invalidation
  const getTokenWithInvalidation = useCallback(async (): Promise<string> => {
    return getToken();
  }, [getToken]);

  // Build SDK client config with dynamic token provider
  const clientConfig = useMemo(
    () => ({
      baseUrl,
      authProvider: createTokenAuthProvider(getTokenWithInvalidation),
      onUnauthorized: onTokenInvalid
    }),
    [baseUrl, getTokenWithInvalidation, onTokenInvalid]
  );

  // SDK hooks for reports list
  const {
    data: reports,
    loading: loadingReports,
    error: reportsError
  } = useReports(clientConfig);

  // Local state for selected report
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

  // SDK hook for report config
  const {
    data: reportConfig,
    loading: loadingConfig,
    error: configError
  } = useReportConfig(clientConfig, selectedReportId);

  // SDK hook for filter state management
  const filterState = useFilterState(reportConfig);

  // Local state for filter validation errors
  const [filterErrors, setFilterErrors] = useState<Record<string, string>>({});

  // Get visible filters (exclude hidden and system filters)
  // Also apply preserveOrder for date-based dropdown filters like "month"
  const visibleFilters = useMemo(() => {
    if (!reportConfig?.filters) return [];
    return reportConfig.filters
      .filter((f: Filter) => !f.hidden && f.source !== 'system')
      .map((f: Filter) => {
        // Month filter should preserve chronological order from API
        if (f.id === 'month' && f.type === 'select') {
          return { ...f, preserveOrder: true };
        }
        return f;
      });
  }, [reportConfig]);

  // SDK hook for dynamic filter options
  const { getOptions, isLoading: isLoadingOptions } = useFilterOptions(
    clientConfig,
    selectedReportId,
    reportConfig?.filters ?? [],
    filterState.values
  );

  // SDK hook for report generation
  const generator = useGenerateReport(clientConfig, selectedReportId);

  const pageSize = 15;

  // Current page and total pages come from the API response
  const pagination = generator.htmlPagination;
  const currentPage = pagination?.currentPage ?? 1;
  const totalPages = pagination?.totalPages ?? 1;

  // Local state to track which format is exporting (so we show spinner on that button only)
  const [exportingFormat, setExportingFormat] = useState<
    'pdf' | 'csv' | 'xlsx' | null
  >(null);
  const isExporting = exportingFormat !== null;

  // Local state to track pagination (so iframe stays visible during page changes)
  const [isPaginating, setIsPaginating] = useState(false);

  // Error display state
  const [dismissedError, setDismissedError] = useState(false);

  /**
   * Reset filter errors when config changes.
   *
   * WHY: When a new report is selected and config loads, clear old errors.
   */
  useEffect(() => {
    setFilterErrors({});
  }, [reportConfig]);

  /**
   * Map SDK validation errors to field-level errors.
   *
   * WHY: When the server returns validation errors, we need to display them
   * next to the relevant filter fields.
   */
  useEffect(() => {
    /**
     * Map server validation errors to field-level errors.
     */
    function handleValidationErrors() {
      if (!generator.error?.isValidationError?.()) {
        return;
      }
      // Access details from the error object
      const errorObj = generator.error;
      const details =
        errorObj && 'details' in errorObj ? errorObj.details : null;
      if (details?.errors && Array.isArray(details.errors)) {
        const serverErrors: Record<string, string> = {};
        details.errors.forEach((err: any) => {
          if (err && err.field && err.message) {
            serverErrors[err.field] = err.message;
          }
        });
        setFilterErrors((prev) => ({ ...prev, ...serverErrors }));
      }
    }
    handleValidationErrors();
  }, [generator.error]);

  /**
   * Handle report selection.
   */
  const handleReportSelect = useCallback(
    (reportId: string) => {
      setSelectedReportId(reportId);
      setDismissedError(false);
      generator.clear();
    },
    [generator]
  );

  /**
   * Handle filter value change.
   */
  const handleFilterChange = useCallback(
    (filterId: string, value: unknown) => {
      filterState.setValue(filterId, value);
      // Clear error for this field
      setFilterErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[filterId];
        return newErrors;
      });
    },
    [filterState]
  );

  /**
   * Handle filter blur (validate on blur).
   */
  const handleFilterBlur = useCallback(
    // eslint-disable-next-line no-unused-vars
    (_filterId: string) => {
      if (!reportConfig?.filters) return;
      const filters = reportConfig.filters;
      const errors = validateAllFilters(filters, filterState.values);
      setFilterErrors(errors);
    },
    [reportConfig, filterState.values]
  );

  /**
   * Generate report (HTML).
   */
  const handleGenerateReport = useCallback(
    async (page = 1) => {
      if (!selectedReportId || !reportConfig) return;

      // Validate all filters
      const filters = reportConfig.filters;
      const errors = validateAllFilters(filters, filterState.values);
      if (Object.keys(errors).length > 0) {
        setFilterErrors(errors);
        return;
      }

      setDismissedError(false);

      // Convert filters to API format and generate HTML
      // The page/pageSize are sent to API; pagination state comes back via generator.htmlPagination
      const apiFilters = convertFiltersToApiFormat(filterState.values);
      await generator.generateHtml(apiFilters, { page, pageSize });
    },
    [selectedReportId, reportConfig, filterState.values, generator, pageSize]
  );

  /**
   * Handle page change for pagination.
   */
  const handlePageChange = useCallback(
    (page: number) => {
      setIsPaginating(true);
      handleGenerateReport(page).finally(() => {
        setIsPaginating(false);
      });
    },
    [handleGenerateReport]
  );

  /**
   * Helper to extract filename from downloadUrl or generate one.
   */
  const getFilenameFromUrl = useCallback(
    (url: string, fallbackExt: string): string => {
      try {
        const pathname = new URL(url).pathname;
        const filename = pathname.split('/').pop();
        if (filename) return filename;
      } catch {
        // URL parsing failed, use fallback
      }
      return `report-${Date.now()}.${fallbackExt}`;
    },
    []
  );

  /**
   * Export CSV.
   */
  const handleExportCsv = useCallback(async () => {
    if (!selectedReportId) return;

    setExportingFormat('csv');
    try {
      const apiFilters = convertFiltersToApiFormat(filterState.values);
      const result = await generator.generateCsv(apiFilters, {
        pageSize: 10000
      });
      if (result?.downloadUrl) {
        const filename = getFilenameFromUrl(result.downloadUrl, 'csv');
        await generator.downloadFile(result.downloadUrl, filename);
      }
    } finally {
      setExportingFormat(null);
    }
  }, [selectedReportId, filterState.values, generator, getFilenameFromUrl]);

  /**
   * Export XLSX (Excel).
   */
  const handleExportXlsx = useCallback(async () => {
    if (!selectedReportId) return;

    setExportingFormat('xlsx');
    try {
      const apiFilters = convertFiltersToApiFormat(filterState.values);
      const result = await generator.generateXlsx(apiFilters, {
        pageSize: 10000
      });
      if (result?.downloadUrl) {
        const filename = getFilenameFromUrl(result.downloadUrl, 'xlsx');
        await generator.downloadFile(result.downloadUrl, filename);
      }
    } finally {
      setExportingFormat(null);
    }
  }, [selectedReportId, filterState.values, generator, getFilenameFromUrl]);

  /**
   * Export PDF.
   */
  const handleExportPdf = useCallback(async () => {
    if (!selectedReportId) return;

    setExportingFormat('pdf');
    try {
      const apiFilters = convertFiltersToApiFormat(filterState.values);
      const result = await generator.generatePdf(apiFilters, {
        pageSize: 10000
      });
      if (result?.downloadUrl) {
        const filename = getFilenameFromUrl(result.downloadUrl, 'pdf');
        await generator.downloadFile(result.downloadUrl, filename);
      }
    } finally {
      setExportingFormat(null);
    }
  }, [selectedReportId, filterState.values, generator, getFilenameFromUrl]);

  /**
   * Dismiss error notification.
   */
  const handleDismissError = useCallback(() => {
    setDismissedError(true);
  }, []);

  // Determine current error to display
  const currentError = reportsError || configError || generator.error;
  const errorMessage = currentError?.message || null;

  // Check if generate button should be enabled
  const isGenerateDisabled =
    !selectedReportId ||
    !reportConfig ||
    generator.loading ||
    !areRequiredFiltersFilled(visibleFilters, filterState.values);

  // Get reports list (default to empty array)
  const reportsList = reports ?? [];

  // Format reports for Carbon Dropdown, sorted alphabetically by name
  const reportItems = useMemo(() => {
    return reportsList
      .map((report) => ({
        ...report,
        label: report.name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [reportsList]);

  const selectedReportItem = useMemo(() => {
    return reportItems.find((item) => item.id === selectedReportId) || null;
  }, [reportItems, selectedReportId]);

  // Skeleton items for filter loading state
  const skeletonItems = ['sk1', 'sk2', 'sk3'];

  return (
    <div className="selection-form">
      {/* Error display */}
      {errorMessage && !dismissedError && (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle={errorMessage}
          onCloseButtonClick={handleDismissError}
          style={{ marginBottom: '1rem' }}
        />
      )}

      {/* Report type selector row: Dropdown + Generate button */}
      <div className="selection-form--report-type">
        <div className="selection-form--dropdown">
          {loadingReports ? (
            <SelectSkeleton style={{ minWidth: '27.5rem' }} />
          ) : reportsList.length > 0 ? (
            <Dropdown
              id="report-type-selector"
              titleText="Report type"
              label="Select..."
              items={reportItems}
              itemToString={(item) => (item ? item.label : '')}
              selectedItem={selectedReportItem}
              onChange={({ selectedItem: selected }) => {
                if (selected) {
                  handleReportSelect(selected.id);
                }
              }}
              disabled={loadingConfig || generator.loading}
              size="xl"
            />
          ) : (
            <p style={{ padding: '1rem 0' }}>No reports available</p>
          )}
        </div>
        <div className="selection-form--button">
          <Button
            type="submit"
            kind="primary"
            data-testid="generateReportBtn"
            onClick={() => handleGenerateReport(1)}
            disabled={isGenerateDisabled}
          >
            <span className="btn-text">Generate report</span>
            {generator.loading && !isExporting && !isPaginating && (
              <Loading
                small
                description="Generating report"
                withOverlay={false}
              />
            )}
          </Button>
        </div>
      </div>

      {/* Filters section - horizontal layout with background */}
      {(loadingConfig || (reportConfig && visibleFilters.length > 0)) && (
        <div className="selection-form--filters">
          <div className="selection-form--filters-body">
            {loadingConfig ? (
              // Skeleton loading for filters
              skeletonItems.map((id) => (
                <SelectSkeleton className="skeleton--item" key={id} />
              ))
            ) : (
              <FilterForm
                filters={visibleFilters}
                values={filterState.values}
                errors={filterErrors}
                onChange={handleFilterChange}
                onBlur={handleFilterBlur}
                getOptions={getOptions}
                isLoadingOptions={isLoadingOptions}
                disabled={generator.loading && !isExporting}
              />
            )}
          </div>
        </div>
      )}

      {/* Async operation status - show during generation but not during export or pagination */}
      {generator.loading &&
        generator.statusMessage &&
        !isExporting &&
        !isPaginating && (
          <div
            style={{
              marginTop: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Loading small withOverlay={false} />
            <span style={{ fontSize: '0.875rem', color: '#525252' }}>
              {generator.statusMessage}
            </span>
          </div>
        )}

      {/* Action tools bar: Download buttons + Pagination */}
      {generator.htmlData &&
        (!generator.loading || isExporting || isPaginating) && (
          <div className="aciton-tools--wrapper">
            <div className="aciton-tools--downloads">
              <span>Download as: </span>
              <Button
                hasIconOnly
                renderIcon={
                  exportingFormat === 'pdf'
                    ? () => (
                        <Loading
                          small
                          description="Exporting PDF"
                          withOverlay={false}
                        />
                      )
                    : Pdf24
                }
                tooltipAlignment="center"
                tooltipPosition="bottom"
                iconDescription="Export the report as PDF"
                size="lg"
                kind="ghost"
                onClick={handleExportPdf}
                disabled={isExporting}
              />
              <Button
                hasIconOnly
                renderIcon={
                  exportingFormat === 'csv'
                    ? () => (
                        <Loading
                          small
                          description="Exporting CSV"
                          withOverlay={false}
                        />
                      )
                    : Csv24
                }
                tooltipAlignment="center"
                tooltipPosition="bottom"
                iconDescription="Export the report as CSV"
                size="lg"
                kind="ghost"
                onClick={handleExportCsv}
                disabled={isExporting}
              />
              <Button
                hasIconOnly
                renderIcon={
                  exportingFormat === 'xlsx'
                    ? () => (
                        <Loading
                          small
                          description="Exporting XLS"
                          withOverlay={false}
                        />
                      )
                    : Xls24
                }
                tooltipAlignment="center"
                tooltipPosition="bottom"
                iconDescription="Export the report as XLS"
                size="lg"
                kind="ghost"
                onClick={handleExportXlsx}
                disabled={isExporting}
              />
            </div>
            <div className="aciton-tools--pagination">
              <ReportActions
                currentPage={currentPage}
                pageSize={pageSize}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                onExportCsv={handleExportCsv}
                onExportXlsx={handleExportXlsx}
                onExportPdf={handleExportPdf}
                loading={isExporting}
                disabled={generator.loading}
                paginationOnly
              />
            </div>
          </div>
        )}

      {/* Report display - HTML rendered by server */}
      {generator.htmlData && (
        <ReportTable
          html={generator.htmlData}
          loading={(generator.loading && !isExporting) || isPaginating}
        />
      )}
    </div>
  );
};

export default React.memo(ReportsCrsViewer);
